<template>
  <Doughnut :chart-data="chartData" :chart-options="mergedOptions" :plugins="plugins" />
</template>

<script setup>
import { ArcElement, CategoryScale, Chart as ChartJS, Legend, Title, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { computed } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { PaletteColors } from '@/Theme';
import merge from 'lodash/merge';
import { getDatasetColor } from '@/utils/chart';

ChartJS.register(ArcElement, CategoryScale, Legend, Title, Tooltip);

const props = defineProps({
  labels: {
    type: Array,
    required: true,
  },
  values: {
    type: Array,
    required: true,
  },
  colors: {
    type: Array,
    default: null,
  },
  chartOptions: {
    type: Object,
    default: () => ({}),
  },
  showDataLabels: {
    type: Boolean,
    default: true,
  },
  cutout: {
    type: [String, Number],
    default: '60%',
  },
});

const defaultColors = computed(() => {
  if (props.colors && props.colors.length >= props.values.length) {
    return props.colors;
  }
  return props.values.map((_, index) => PaletteColors[getDatasetColor(index)]);
});

const chartData = computed(() => ({
  labels: props.labels,
  datasets: [
    {
      backgroundColor: defaultColors.value,
      data: props.values,
      borderWidth: 0,
    },
  ],
}));

const defaultOptions = computed(() => ({
  cutout: props.cutout,
  maintainAspectRatio: true,
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      backgroundColor: PaletteColors['default'],
      padding: 12,
      cornerRadius: 4,
      callbacks: {
        label: context => {
          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
          const percentage = total > 0 ? Math.round((context.raw / total) * 100) : 0;
          return `${context.label}: ${percentage}%`;
        },
      },
    },
    datalabels: props.showDataLabels
      ? {
          color: 'white',
          font: {
            weight: 'bold',
            size: 11,
          },
          formatter: (value, context) => {
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return percentage >= 5 ? `${percentage}%` : '';
          },
        }
      : { display: false },
  },
}));

const mergedOptions = computed(() => merge({}, defaultOptions.value, props.chartOptions));

const plugins = computed(() => (props.showDataLabels ? [ChartDataLabels] : []));
</script>
